import type { Language } from './i18n';
import { docLabel } from './reports/docStrings';

/* =============================================================================
 * Which name to show
 * -----------------------------------------------------------------------------
 * An employee has two names on file: the one on the passport, in Latin script,
 * and the one in Arabic. Which of them belongs on screen is one decision, and it
 * was being taken separately in every place that showed a name — the monthly
 * grid, the employee list, the payroll exports — each with its own `?? null`.
 *
 * The rule is small enough to state once: in Arabic, show the Arabic name if the
 * office has recorded one, and otherwise show the Latin one. Never show a blank
 * where a name should be, and never show the key.
 *
 * The fallback is not a formality. `full_name_ar` is optional and, on a thousand
 * imported records, will be empty for most of them for a long time — an Arabic
 * screen that showed nothing wherever it was missing would be worse than one
 * that shows the passport spelling.
 * ========================================================================== */

interface Named {
  full_name: string;
  full_name_ar?: string | null;
}

export function personName(person: Named, language: Language): string {
  if (language === 'ar' && person.full_name_ar) return person.full_name_ar;
  return person.full_name;
}

/** The same rule for anything else carrying a name in two scripts. */
export function bilingualName(
  latin: string,
  arabic: string | null | undefined,
  language: Language,
): string {
  return language === 'ar' && arabic ? arabic : latin;
}

/* =============================================================================
 * The same rule, for everything else the company named
 * -----------------------------------------------------------------------------
 * A holiday, a role, a leave type, an allowance, a break. Each stores the words
 * the company typed and, optionally, the same words in Arabic. There is one
 * question — which of the two belongs on this screen — and it now has one
 * answer, called from the places that ask it.
 * ========================================================================== */

/** A record carrying a name and, perhaps, its Arabic. */
interface NamedRecord {
  name: string;
  name_ar?: string | null;
  nameAr?: string | null;
}

/** A record carrying a label and, perhaps, its Arabic. */
interface LabelledRecord {
  label: string;
  label_ar?: string | null;
  labelAr?: string | null;
}

/**
 * The name of a holiday, role, leave type or allowance, in the reader's
 * language.
 *
 * Both spellings of the Arabic field are accepted deliberately: the tables use
 * `name_ar` because that is how this schema names columns, and the policy
 * document uses `nameAr` because that is how it names everything else. Making
 * each caller know which one it is holding would put the decision back where
 * this function exists to take it away from.
 */
export function nameOf(record: NamedRecord, language: Language): string {
  return bilingualName(record.name, record.name_ar ?? record.nameAr, language);
}

/** The label of a break slot or a salary component, in the reader's language. */
export function labelOf(record: LabelledRecord, language: Language): string {
  return bilingualName(record.label, record.label_ar ?? record.labelAr, language);
}

/* -----------------------------------------------------------------------------
 * Job titles, which have one more place to look
 * -----------------------------------------------------------------------------
 * A designation resolves through three steps rather than two. The company's own
 * `name_ar` comes first, because a firm that has written down what it calls a
 * trade has settled the question. Failing that, the export dictionary already
 * holds the common trades — mason, steel fixer, foreman — because the payslips
 * needed them before any of this existed, and a title it knows is better than
 * the English. Only then does it fall back to the words as typed.
 *
 * The middle step is what makes an Arabic screen useful on day one, before
 * anybody has filled in a single Arabic name: most of a construction company's
 * designations are the same twenty trades.
 * -------------------------------------------------------------------------- */

export function designationName(
  record: NamedRecord | null | undefined,
  language: Language,
): string {
  if (!record) return '';
  if (language === 'ar' && (record.name_ar || record.nameAr)) {
    return (record.name_ar || record.nameAr) as string;
  }
  return docLabel(record.name, language === 'ar' ? 'ar' : 'en');
}
